import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import api from '@/services/api';
import { southWelcomeRuleService } from '@/services/southWelcomeRuleService';
import type { SouthWelcomeHistoryResponse } from '@/types/southWelcome';

interface TagOption { id: number; tag_name: string; tag_code: string; }
interface Props { clientId: number; }

const WEBHOOK_PREFIX = 'https://automation.atomoschat.com.br/webhooks/aeasy/';

const DEFAULTS = { cnpj: '', tag_id: 0, is_active: false };

export const SouthWelcomeTab: React.FC<Props> = ({ clientId }) => {
  const [form, setForm] = useState<typeof DEFAULTS>(DEFAULTS);
  const [tags, setTags] = useState<TagOption[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<SouthWelcomeHistoryResponse['data']>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await southWelcomeRuleService.getByClient(clientId);
        if (!mounted) return;
        setForm({ cnpj: r.cnpj || '', tag_id: r.tag_id || 0, is_active: !!r.is_active });
      } catch (e) { console.error('[SouthWelcome] carregar:', e); }
    })();
    return () => { mounted = false; };
  }, [clientId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingTags(true);
      try {
        const res = (await api.get(`/tags/client/${clientId}/type/BOAS_VINDAS`)) as { success: boolean; data?: TagOption[] };
        if (!mounted) return;
        setTags(res.success ? res.data || [] : []);
      } catch (e) { console.error('[SouthWelcome] tags:', e); }
      finally { if (mounted) setLoadingTags(false); }
    })();
    return () => { mounted = false; };
  }, [clientId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await southWelcomeRuleService.listHistory(clientId, 1, 50);
        if (mounted) setHistory(r.data || []);
      } catch (e) { console.error('[SouthWelcome] histórico:', e); }
    })();
    return () => { mounted = false; };
  }, [clientId]);

  const cnpjDigits = form.cnpj.replace(/\D/g, '');
  const webhookUrl = `${WEBHOOK_PREFIX}${cnpjDigits || '{cnpj}'}`;

  async function handleCopy() {
    try { await navigator.clipboard.writeText(webhookUrl); toast({ title: 'Copiado', description: 'Link do webhook copiado.' }); }
    catch { toast({ title: 'Erro', description: 'Não foi possível copiar.', variant: 'destructive' }); }
  }

  async function handleSave() {
    if (cnpjDigits.length < 11) { toast({ title: 'Erro', description: 'Informe um CNPJ válido.', variant: 'destructive' }); return; }
    if (!form.tag_id) { toast({ title: 'Erro', description: 'Selecione uma tag de Boas-Vindas.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await southWelcomeRuleService.upsert(clientId, { cnpj: cnpjDigits, tag_id: form.tag_id, is_active: form.is_active });
      toast({ title: 'Salvo', description: 'Boas-vindas South atualizada.' });
    } catch (e: any) {
      toast({ title: 'Erro ao salvar', description: e?.message || 'Erro ao salvar.', variant: 'destructive' });
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Boas-Vindas (South)</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
              <Label>{form.is_active ? 'Ativo' : 'Inativo'}</Label>
            </div>

            <div>
              <Label>CNPJ</Label>
              <Input value={form.cnpj} onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))} placeholder="46679616000131" />
              <p className="text-xs text-gray-500">Identifica este cliente no webhook do South.</p>
            </div>

            <div>
              <Label>Link do webhook (configurar no South)</Label>
              <div className="flex gap-2">
                <Input readOnly value={webhookUrl} className="font-mono text-xs" />
                <Button type="button" variant="outline" onClick={handleCopy}>Copiar</Button>
              </div>
            </div>

            <div>
              <Label>Tag de mensagem</Label>
              <Select
                value={form.tag_id ? String(form.tag_id) : ''}
                onValueChange={(v) => setForm((f) => ({ ...f, tag_id: Number(v) }))}
                disabled={loadingTags}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingTags ? 'Carregando...' : 'Selecione uma tag'} />
                </SelectTrigger>
                <SelectContent>
                  {tags.length === 0 && !loadingTags && (
                    <SelectItem value="0" disabled>Nenhuma tag — cadastre em Mensagens</SelectItem>
                  )}
                  {tags.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.tag_name || t.tag_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Histórico de envios</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-4">Data</th>
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">Telefone</th>
                  <th className="py-2 pr-4">Canal</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Erro</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-gray-400">Nenhum envio ainda.</td></tr>
                )}
                {history.map((h) => (
                  <tr key={h.id} className="border-t">
                    <td className="py-2 pr-4">{h.executed_at ? new Date(h.executed_at).toLocaleString('pt-BR') : '-'}</td>
                    <td className="py-2 pr-4">{h.nome || '-'}</td>
                    <td className="py-2 pr-4">{h.telefone || '-'}</td>
                    <td className="py-2 pr-4">{h.canal ? h.canal.toUpperCase() : '-'}</td>
                    <td className="py-2 pr-4">{h.status}</td>
                    <td className="py-2 pr-4">{h.delivery_error_pt || h.error_message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SouthWelcomeTab;
